import { useState } from 'react';
import { AlertTriangle, ChevronDown, Info } from 'lucide-react';
import { isStandalone } from '@/lib/push';
import {
  GUIDE_PLATFORMS, PUSH_GUIDES, describeDevice, detectDevice, deviceNotices, guideFor, type GuidePlatform,
} from '@/lib/push-guide';

/** 背景推播的平台教學。預設打開偵測到的平台，猜錯時可以自己切換。
 *  還沒開啟推播時預設展開；已經開啟的人只在收不到時才需要，預設收合。 */
export function PushGuide({ defaultOpen = false }: { defaultOpen?: boolean }) {
  const [info] = useState(() => detectDevice(navigator.userAgent, navigator.maxTouchPoints));
  const [standalone] = useState(isStandalone);
  const [platform, setPlatform] = useState<GuidePlatform>(() => guideFor(info));
  const [open, setOpen] = useState(defaultOpen);
  const guide = PUSH_GUIDES[platform];
  const device = describeDevice(info);
  const notices = deviceNotices(info);

  return (
    <details className="push-guide" open={open} onToggle={(event) => setOpen(event.currentTarget.open)}>
      <summary>
        <span className="push-guide-summary">
          各平台設定教學
          <ChevronDown className="push-guide-chevron h-4 w-4" aria-hidden="true" />
        </span>
      </summary>
      <div className="push-guide-body">
        <p className="text-[12.5px] leading-relaxed text-muted-foreground">
          {device ? `偵測到：${device}` : '沒有認出這台裝置，請選擇最接近的平台。'}
        </p>
        {notices.map((text) => (
          <p key={text} className="push-guide-notice" data-tone="warn">
            <AlertTriangle className="h-4 w-4" aria-hidden="true" />{text}
          </p>
        ))}
        <div role="radiogroup" aria-label="選擇平台" className="segmented push-guide-platforms">
          {GUIDE_PLATFORMS.map((key) => (
            <button key={key} type="button" role="radio" aria-checked={platform === key} onClick={() => setPlatform(key)}>
              {PUSH_GUIDES[key].tab}
            </button>
          ))}
        </div>
        <p className="push-guide-answer">{guide.answer}</p>
        {platform === 'ios' && standalone && (
          <p className="push-guide-notice" data-tone="info">
            <Info className="h-4 w-4" aria-hidden="true" />你已經從主畫面開啟平台，可以直接從第 4 步開始。
          </p>
        )}
        <ol role="list" className="push-guide-steps">
          {guide.steps.map((step) => <li key={step}>{step}</li>)}
        </ol>
        <div>
          <h4 className="push-guide-notes-title">收不到通知時</h4>
          <ul role="list" className="push-guide-notes">
            {guide.notes.map((note) => <li key={note}>{note}</li>)}
          </ul>
        </div>
      </div>
    </details>
  );
}
